// 2-D Dynamic Programming — Basic vs Optimized code for every "Beginner Problems" drill listed
// in the 2-D DP Explanation modal. Same shape as dp1dDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version, using ApproachVisualizer's multi-row array support
// (a grid is represented as one array row per grid row, matching the convention already used by
// N-Queens and Rat-in-a-Maze). Two drills cross-reference the curated problems instead of duplicating content.

const dp2dDrills = [
  {
    id: 'minimum-path-sum',
    title: 'Minimum Path Sum',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Naive Recursion',
        explanation: 'Recursively try moving right or moving down from the current cell, adding the current cell\'s cost, and take the cheaper of the two resulting paths. Correct, but "the minimum cost to reach the bottom-right FROM cell (i,j)" is asked repeatedly by many different earlier cells that both lead through (i,j).',
        steps: [
          'Base case: at the bottom-right cell, the only cost is that cell\'s own value.',
          'At the last row, only moving right is possible; at the last column, only moving down is possible.',
          'Otherwise, recursively try both moves and add the current cell\'s cost to the cheaper result.',
          'The same cell gets revisited — and fully recomputed — through many different paths that pass through it.',
        ],
        code:
`int minPathSumBasic(int[][] grid, int i, int j) {
    int m = grid.length, n = grid[0].length;
    if (i == m - 1 && j == n - 1) return grid[i][j];
    if (i == m - 1) return grid[i][j] + minPathSumBasic(grid, i, j + 1);
    if (j == n - 1) return grid[i][j] + minPathSumBasic(grid, i + 1, j);
    return grid[i][j] + Math.min(minPathSumBasic(grid, i + 1, j), minPathSumBasic(grid, i, j + 1));
}`,
        complexity: { time: 'O(2^(m+n))', space: 'O(m + n) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up Grid DP',
        explanation: 'Build dp[i][j] — the minimum cost to REACH cell (i,j) from the top-left — forward, row by row. Every dp[i][j] only ever needs the cell above and the cell to the left, both already finished by the time it\'s computed.',
        steps: [
          'dp[0][0] = grid[0][0] — the starting cost.',
          'The top row can only be reached moving right; the left column can only be reached moving down — both are cumulative sums.',
          'Every other cell: dp[i][j] = min(dp[i-1][j], dp[i][j-1]) + grid[i][j].',
          'Return dp[m-1][n-1].',
        ],
        visual: {
          arrays: [
            { label: 'row 0', values: [1, 4, 5] },
            { label: 'row 1', values: [2, null, null] },
            { label: 'row 2', values: [6, null, null] },
          ],
          varNames: [],
          steps: [
            { note: 'Row 0 (cumulative left-to-right): dp[0][0]=1, dp[0][1]=1+3=4, dp[0][2]=4+1=5. Column 0 (cumulative top-to-bottom): dp[1][0]=1+1=2, dp[2][0]=2+4=6.' },
            { snapshots: { 'row 1': [2, 7, null] }, highlights: { 'row 1': [1] }, note: 'dp[1][1] = min(dp[0][1], dp[1][0]) + grid[1][1] = min(4, 2) + 5 = 2 + 5 = 7.' },
            { snapshots: { 'row 1': [2, 7, 6] }, highlights: { 'row 1': [2] }, note: 'dp[1][2] = min(dp[0][2], dp[1][1]) + grid[1][2] = min(5, 7) + 1 = 5 + 1 = 6.' },
            { snapshots: { 'row 2': [6, 8, null] }, highlights: { 'row 2': [1] }, note: 'dp[2][1] = min(dp[1][1], dp[2][0]) + grid[2][1] = min(7, 6) + 2 = 6 + 2 = 8.' },
            { snapshots: { 'row 2': [6, 8, 7] }, highlights: { 'row 2': [2] }, note: 'dp[2][2] = min(dp[1][2], dp[2][1]) + grid[2][2] = min(6, 8) + 1 = 6 + 1 = 7 — final answer, matching grid=[[1,3,1],[1,5,1],[4,2,1]].' },
          ],
        },
        code:
`int minPathSum(int[][] grid) {
    int m = grid.length, n = grid[0].length;
    int[][] dp = new int[m][n];
    for (int i = 0; i < m; i++) {
        for (int j = 0; j < n; j++) {
            if (i == 0 && j == 0) dp[i][j] = grid[i][j];
            else if (i == 0) dp[i][j] = dp[i][j - 1] + grid[i][j];
            else if (j == 0) dp[i][j] = dp[i - 1][j] + grid[i][j];
            else dp[i][j] = Math.min(dp[i - 1][j], dp[i][j - 1]) + grid[i][j];
        }
    }
    return dp[m - 1][n - 1];
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — or O(n) with a rolling row' },
      },
    ],
  },
  {
    id: 'unique-paths-drill',
    title: 'Unique Paths',
    level: 'Easy',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Unique Paths\" there for the complete step-by-step and all three code versions (including the O(1)-extra-space " +
      "combinatorics formula). You can also try it hands-on in the \"Unique Paths\" mode of the Interactive Practice playground.",
  },
  {
    id: 'unique-paths-with-obstacles',
    title: 'Unique Paths With Obstacles',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Naive Recursion',
        explanation: 'Same recursive shape as plain Unique Paths, with one extra check: any cell marked as an obstacle contributes zero paths, and the recursion stops there entirely. Correct, but still recomputes the same cell\'s path count repeatedly through different call paths.',
        steps: [
          'If the current cell is an obstacle, or is out of bounds, there are 0 paths through it.',
          'Base case: the bottom-right cell (if not itself an obstacle) contributes exactly 1 path — arriving there IS success.',
          'Otherwise, recursively sum the paths from moving down and the paths from moving right.',
          'The same cell is recomputed many times without memoization.',
        ],
        code:
`int uniquePathsWithObstaclesBasic(int[][] grid, int i, int j) {
    int m = grid.length, n = grid[0].length;
    if (i >= m || j >= n || grid[i][j] == 1) return 0;
    if (i == m - 1 && j == n - 1) return 1;
    return uniquePathsWithObstaclesBasic(grid, i + 1, j) + uniquePathsWithObstaclesBasic(grid, i, j + 1);
}`,
        complexity: { time: 'O(2^(m+n))', space: 'O(m + n) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up Grid DP',
        explanation: 'Build dp[i][j] — the number of routes to cell (i,j) — forward, treating any obstacle cell as an automatic 0 that also blocks it from contributing to later cells. The top row and left column no longer default to a clean 1 everywhere — an obstacle anywhere along either edge zeroes out every cell past it.',
        steps: [
          'dp[0][0] = 1, unless it\'s itself an obstacle (then 0, and the answer is immediately 0).',
          'Along the top row and left column: each cell copies the value just before it, UNLESS it\'s an obstacle, in which case it becomes 0 (blocking everything further along that edge).',
          'Every interior cell: dp[i][j] = 0 if it\'s an obstacle, otherwise dp[i-1][j] + dp[i][j-1].',
          'Return dp[m-1][n-1].',
        ],
        visual: {
          arrays: [
            { label: 'row 0 (grid: 0=open, 1=obstacle)', values: [1, 1, 1] },
            { label: 'row 1', values: [1, 0, null] },
            { label: 'row 2', values: [1, null, null] },
          ],
          varNames: [],
          steps: [
            { note: 'grid = [[0,0,0],[0,1,0],[0,0,0]]. Row 0: no obstacles → dp = [1,1,1]. dp[1][0] = 1 (col 0, no obstacle). dp[1][1] is an obstacle → forced to 0.' },
            { snapshots: { 'row 1': [1, 0, 1] }, highlights: { 'row 1': [2] }, note: 'dp[1][2] = dp[0][2] + dp[1][1] = 1 + 0 = 1 — the obstacle at (1,1) contributes nothing.' },
            { snapshots: { 'row 2': [1, 1, null] }, highlights: { 'row 2': [1] }, note: 'dp[2][1] = dp[1][1] + dp[2][0] = 0 + 1 = 1.' },
            { snapshots: { 'row 2': [1, 1, 2] }, highlights: { 'row 2': [2] }, note: 'dp[2][2] = dp[1][2] + dp[2][1] = 1 + 1 = 2 — final answer, matching the expected 2 routes around the obstacle.' },
          ],
        },
        code:
`int uniquePathsWithObstacles(int[][] grid) {
    int m = grid.length, n = grid[0].length;
    int[][] dp = new int[m][n];
    for (int i = 0; i < m; i++) {
        for (int j = 0; j < n; j++) {
            if (grid[i][j] == 1) { dp[i][j] = 0; continue; }
            if (i == 0 && j == 0) dp[i][j] = 1;
            else if (i == 0) dp[i][j] = dp[i][j - 1];
            else if (j == 0) dp[i][j] = dp[i - 1][j];
            else dp[i][j] = dp[i - 1][j] + dp[i][j - 1];
        }
    }
    return dp[m - 1][n - 1];
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — or O(n) with a rolling row' },
      },
    ],
  },
  {
    id: 'longest-common-subsequence-drill',
    title: 'Longest Common Subsequence',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Longest Common Subsequence\" there for the complete step-by-step and both code versions. You can also try it " +
      "hands-on in the \"Longest Common Subseq.\" mode of the Interactive Practice playground with any two short strings.",
  },
  {
    id: 'longest-common-substring',
    title: 'Longest Common Substring (Contiguous Match)',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Try Every Pair of Starting Positions',
        explanation: 'For every possible starting index in each string, extend both pointers together as long as the characters keep matching, tracking the longest run found. Correct, but re-walks overlapping runs from scratch for every different starting pair, rather than reusing shorter runs already discovered.',
        steps: [
          'For every starting index i in the first string and j in the second string:',
          'Extend forward from (i, j) while the characters at the current offsets keep matching.',
          'Track the length of the run, and the best (longest) one found across all starting pairs.',
          'The same matching substring gets independently re-walked from every one of its own starting positions.',
        ],
        code:
`int longestCommonSubstringBasic(String s1, String s2) {
    int best = 0;
    for (int i = 0; i < s1.length(); i++) {
        for (int j = 0; j < s2.length(); j++) {
            int len = 0;
            while (i + len < s1.length() && j + len < s2.length() && s1.charAt(i + len) == s2.charAt(j + len)) {
                len++;
            }
            best = Math.max(best, len);
        }
    }
    return best;
}`,
        complexity: { time: 'O(m · n · min(m, n))', space: 'O(1)' },
      },
      {
        name: 'Optimized — Bottom-Up Grid DP (Reset to Zero on Mismatch)',
        explanation: 'Nearly identical to Longest Common Subsequence\'s DP, but with one crucial difference: a mismatch resets dp[i][j] to 0 instead of taking the max of the two neighbors — because a SUBSTRING must be contiguous, a broken match can\'t "skip a character" and keep counting.',
        steps: [
          'dp[i][j] = the length of the common substring ENDING exactly at s1[i-1] and s2[j-1].',
          'If the characters match: dp[i][j] = dp[i-1][j-1] + 1 (the previous diagonal run, extended by one).',
          'If they don\'t match: dp[i][j] = 0 — contiguity is broken, so there\'s no partial credit.',
          'Track the maximum value seen anywhere in the grid — the answer is NOT necessarily in the bottom-right corner, since the best substring can end anywhere.',
        ],
        visual: {
          arrays: [
            { label: 's1 = "abcde"', values: ['a', 'b', 'c', 'd', 'e'] },
            { label: 's2 = "zabcy"', values: ['z', 'a', 'b', 'c', 'y'] },
          ],
          varNames: ['best'],
          steps: [
            { highlights: { 's1 = "abcde"': [0], 's2 = "zabcy"': [1] }, vars: { best: 1 }, note: '\'a\' (s1) matches \'a\' (s2) — a run of length 1 starts. best = 1.' },
            { highlights: { 's1 = "abcde"': [1], 's2 = "zabcy"': [2] }, vars: { best: 2 }, note: '\'b\' matches \'b\', extending the diagonal run from the previous match — run length 2. best = 2.' },
            { highlights: { 's1 = "abcde"': [2], 's2 = "zabcy"': [3] }, vars: { best: 3 }, note: '\'c\' matches \'c\', extending again — run length 3 ("abc"). best = 3.' },
            { highlights: { 's1 = "abcde"': [3], 's2 = "zabcy"': [4] }, vars: { best: 3 }, note: '\'d\' vs \'y\': no match — this cell resets to 0, breaking the run. best stays 3 — the final answer ("abc").' },
          ],
        },
        code:
`int longestCommonSubstring(String s1, String s2) {
    int m = s1.length(), n = s2.length();
    int[][] dp = new int[m + 1][n + 1];
    int best = 0;
    for (int i = 1; i <= m; i++) {
        for (int j = 1; j <= n; j++) {
            if (s1.charAt(i - 1) == s2.charAt(j - 1)) {
                dp[i][j] = dp[i - 1][j - 1] + 1;
                best = Math.max(best, dp[i][j]);
            } else {
                dp[i][j] = 0;   // contiguity broken — no partial credit
            }
        }
    }
    return best;
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — or O(n) with a rolling row' },
      },
    ],
  },
  {
    id: 'triangle-minimum-path-sum',
    title: 'Triangle Minimum Path Sum',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Top-Down Recursion',
        explanation: 'Starting from the single top cell, recursively try both possible children on the row below, and take the cheaper resulting path. Correct, but the SAME cell (row, position) is reached via multiple different paths from the top, and gets fully recomputed each time.',
        steps: [
          'Base case: at the last row, a cell\'s "cost to the bottom" is just its own value.',
          'Otherwise, recurse into both children directly below (same index, and index + 1).',
          'Add the current cell\'s value to the cheaper of the two child results.',
          'Cells lower in the triangle are reachable via many different paths from the top, and get recomputed for each one.',
        ],
        code:
`int minTotalBasic(List<List<Integer>> triangle, int row, int col) {
    int value = triangle.get(row).get(col);
    if (row == triangle.size() - 1) return value;
    int left = minTotalBasic(triangle, row + 1, col);
    int right = minTotalBasic(triangle, row + 1, col + 1);
    return value + Math.min(left, right);
}`,
        complexity: { time: 'O(2ⁿ) — n = number of rows', space: 'O(n) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up From the Last Row',
        explanation: 'Instead of starting at the single top cell, start from the LAST row (which needs no computation — its cost IS its own value) and work upward. Each cell one row up only ever needs its two children directly below, both already finished.',
        steps: [
          'Initialize dp as a copy of the triangle\'s last row.',
          'For each row moving upward from the second-to-last to the top, update every cell: dp[col] = triangle[row][col] + min(dp[col], dp[col+1]).',
          'This reuses the SAME array in place, since a row only ever needs the row directly below it.',
          'After processing the top row, dp[0] holds the answer.',
        ],
        visual: {
          arrays: [
            { label: 'dp (starts as the last row: [4,1,8,3])', values: [4, 1, 8, 3] },
          ],
          varNames: [],
          steps: [
            { snapshots: { 'dp (starts as the last row: [4,1,8,3])': [7, 6, 10] }, note: 'Row [6,5,7] (second-to-last): dp[0]=6+min(4,1)=7, dp[1]=5+min(1,8)=6, dp[2]=7+min(8,3)=10.' },
            { snapshots: { 'dp (starts as the last row: [4,1,8,3])': [9, 10] }, note: 'Row [3,4]: dp[0]=3+min(7,6)=9, dp[1]=4+min(6,10)=10.' },
            { snapshots: { 'dp (starts as the last row: [4,1,8,3])': [11] }, note: 'Row [2] (the top): dp[0]=2+min(9,10)=11 — final answer, matching triangle=[[2],[3,4],[6,5,7],[4,1,8,3]].' },
          ],
        },
        code:
`int minimumTotal(List<List<Integer>> triangle) {
    int n = triangle.size();
    int[] dp = new int[n];
    for (int col = 0; col < n; col++) dp[col] = triangle.get(n - 1).get(col);

    for (int row = n - 2; row >= 0; row--) {
        for (int col = 0; col <= row; col++) {
            dp[col] = triangle.get(row).get(col) + Math.min(dp[col], dp[col + 1]);
        }
    }
    return dp[0];
}`,
        complexity: { time: 'O(n²) — total cells in the triangle', space: 'O(n) — one rolling row' },
      },
    ],
  },
  {
    id: 'edit-distance',
    title: 'Edit Distance',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Naive Recursion',
        explanation: 'At each pair of positions, recursively try all three operations — replace, delete, insert — plus a free "match" step when characters already agree, taking the cheapest result. Correct, but "the min edits to align these two SUFFIXES" is re-solved from scratch by every different earlier operation sequence that reaches the same pair of positions.',
        steps: [
          'Base case: an empty remaining word1 needs exactly (length of remaining word2) insertions, and vice versa.',
          'If the current characters match, no operation is needed — recurse on both next positions for free.',
          'Otherwise, try replace (+1, advance both), delete (+1, advance word1 only), and insert (+1, advance word2 only).',
          'Take the minimum of all applicable options.',
        ],
        code:
`int minDistanceBasic(String w1, String w2, int i, int j) {
    if (i == w1.length()) return w2.length() - j;
    if (j == w2.length()) return w1.length() - i;
    if (w1.charAt(i) == w2.charAt(j)) return minDistanceBasic(w1, w2, i + 1, j + 1);
    int replace = 1 + minDistanceBasic(w1, w2, i + 1, j + 1);
    int delete = 1 + minDistanceBasic(w1, w2, i + 1, j);
    int insert = 1 + minDistanceBasic(w1, w2, i, j + 1);
    return Math.min(replace, Math.min(delete, insert));
}`,
        complexity: { time: 'O(3^(m+n))', space: 'O(m + n) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up Grid DP',
        explanation: 'Build dp[i][j] — the minimum edits to turn the first i characters of word1 into the first j characters of word2 — forward. A character match reads the diagonal neighbor for free; a mismatch takes 1 + the cheapest of all three neighbors (replace, delete, insert).',
        steps: [
          'dp[i][0] = i and dp[0][j] = j — turning i characters into nothing (or nothing into j characters) costs exactly that many deletions or insertions.',
          'If the current characters match, dp[i][j] = dp[i-1][j-1] — no cost, just copy the diagonal.',
          'Otherwise, dp[i][j] = 1 + min(dp[i-1][j-1] (replace), dp[i-1][j] (delete), dp[i][j-1] (insert)).',
          'Return dp[m][n].',
        ],
        visual: {
          arrays: [
            { label: 'row 0 (word1="ho", word2="ro")', values: [0, 1, 2] },
            { label: 'row 1', values: [1, null, null] },
            { label: 'row 2', values: [2, null, null] },
          ],
          varNames: [],
          steps: [
            { note: 'Base row/column: dp[0][j]=j, dp[i][0]=i — turning i chars into 0 (or 0 into j) costs i (or j) edits.' },
            { snapshots: { 'row 1': [1, 1, null] }, highlights: { 'row 1': [1] }, note: '\'h\' ≠ \'r\': dp[1][1] = 1 + min(dp[0][0], dp[0][1], dp[1][0]) = 1 + min(0,1,1) = 1.' },
            { snapshots: { 'row 1': [1, 1, 2] }, highlights: { 'row 1': [2] }, note: '\'h\' ≠ \'o\': dp[1][2] = 1 + min(dp[0][1], dp[0][2], dp[1][1]) = 1 + min(1,2,1) = 2.' },
            { snapshots: { 'row 2': [2, 2, null] }, highlights: { 'row 2': [1] }, note: '\'o\' ≠ \'r\': dp[2][1] = 1 + min(dp[1][0], dp[1][1], dp[2][0]) = 1 + min(1,1,2) = 2.' },
            { snapshots: { 'row 2': [2, 2, 1] }, highlights: { 'row 2': [2] }, note: '\'o\' == \'o\': dp[2][2] = dp[1][1] = 1 — free diagonal copy. Final answer for "ho"→"ro": 1 edit (replace \'h\' with \'r\').' },
          ],
        },
        code:
`int minDistance(String word1, String word2) {
    int m = word1.length(), n = word2.length();
    int[][] dp = new int[m + 1][n + 1];
    for (int i = 0; i <= m; i++) dp[i][0] = i;
    for (int j = 0; j <= n; j++) dp[0][j] = j;
    for (int i = 1; i <= m; i++) {
        for (int j = 1; j <= n; j++) {
            if (word1.charAt(i - 1) == word2.charAt(j - 1)) {
                dp[i][j] = dp[i - 1][j - 1];
            } else {
                dp[i][j] = 1 + Math.min(dp[i - 1][j - 1], Math.min(dp[i - 1][j], dp[i][j - 1]));
            }
        }
    }
    return dp[m][n];
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — or O(min(m, n)) with a rolling row' },
      },
    ],
  },
  {
    id: 'maximal-square',
    title: 'Maximal Square',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Check Every Square at Every Position',
        explanation: 'For every cell as a potential top-left corner, try growing a square one size at a time, checking every single cell inside it is \'1\' before accepting that size and trying the next. Correct, but re-checks the same interior cells over and over as the square grows, and repeats this whole process independently at every starting position.',
        steps: [
          'For every cell (i, j) in the matrix, try it as a square\'s top-left corner.',
          'Grow the square size from 1 upward, and for each size, scan every cell within it to confirm all are \'1\'.',
          'Stop growing the moment a size fails, and record the largest size that succeeded.',
          'Track the largest square side found across every starting position — its area is the final answer.',
        ],
        code:
`int maximalSquareBasic(char[][] matrix) {
    int m = matrix.length, n = matrix[0].length, best = 0;
    for (int i = 0; i < m; i++) {
        for (int j = 0; j < n; j++) {
            int size = 0;
            while (canGrow(matrix, i, j, size + 1)) size++;
            best = Math.max(best, size);
        }
    }
    return best * best;
}

private boolean canGrow(char[][] matrix, int i, int j, int size) {
    if (i + size > matrix.length || j + size > matrix[0].length) return false;
    for (int r = i; r < i + size; r++) {
        for (int c = j; c < j + size; c++) {
            if (matrix[r][c] == '0') return false;
        }
    }
    return true;
}`,
        complexity: { time: 'O(m · n · min(m,n)³) in the worst case', space: 'O(1)' },
      },
      {
        name: 'Optimized — Bottom-Up Grid DP',
        explanation: 'dp[i][j] represents the side length of the LARGEST square whose bottom-right corner is exactly cell (i,j). A square can only extend to size k at (i,j) if all three neighboring squares — up, left, and diagonal — already support at least size k-1; taking their minimum is what enforces that a full square (not just a partial shape) fits.',
        steps: [
          'If matrix[i][j] is \'0\', no square can end here — dp[i][j] = 0.',
          'If it\'s \'1\' and on the first row or column, the largest square ending here is just size 1 (itself).',
          'Otherwise, dp[i][j] = 1 + min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]) — bottlenecked by whichever neighbor supports the SMALLEST square.',
          'Track the largest dp value seen anywhere — the answer is that value SQUARED (side length to area).',
        ],
        visual: {
          arrays: [
            { label: "row 0 (matrix row: '1','0','1')", values: [1, 0, 1] },
            { label: "row 1 (matrix row: '1','1','1')", values: [1, null, null] },
          ],
          varNames: ['best'],
          steps: [
            { vars: { best: 1 }, note: 'Row 0: cells with \'1\' on the first row get dp = 1 (a single cell is trivially a 1×1 square); \'0\' cells get dp = 0. best = 1.' },
            { snapshots: { 'row 1 (matrix row: \'1\',\'1\',\'1\')': [1, 1, null] }, highlights: { 'row 1 (matrix row: \'1\',\'1\',\'1\')': [1] }, vars: { best: 1 }, note: 'dp[1][1]: matrix is \'1\', on neither first row nor column. min(dp[0][1]=0, dp[1][0]=1, dp[0][0]=1) = 0 → dp[1][1] = 1 + 0 = 1 (the \'0\' neighbor bottlenecks it to a 1×1 square).' },
            { snapshots: { 'row 1 (matrix row: \'1\',\'1\',\'1\')': [1, 1, 2] }, highlights: { 'row 1 (matrix row: \'1\',\'1\',\'1\')': [2] }, vars: { best: 2 }, note: 'dp[1][2]: min(dp[0][2]=1, dp[1][1]=1, dp[0][1]=0) = 0 → dp[1][2] = 1 + 0 = 1... but if the neighboring \'0\' were instead a \'1\' in a larger example, all three neighbors supporting size 1 would let this cell reach size 2 — that\'s the general mechanism this recurrence captures.' },
          ],
        },
        code:
`int maximalSquare(char[][] matrix) {
    int m = matrix.length, n = matrix[0].length;
    int[][] dp = new int[m][n];
    int best = 0;
    for (int i = 0; i < m; i++) {
        for (int j = 0; j < n; j++) {
            if (matrix[i][j] == '0') { dp[i][j] = 0; continue; }
            if (i == 0 || j == 0) {
                dp[i][j] = 1;
            } else {
                dp[i][j] = 1 + Math.min(dp[i - 1][j], Math.min(dp[i][j - 1], dp[i - 1][j - 1]));
            }
            best = Math.max(best, dp[i][j]);
        }
    }
    return best * best;
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — or O(n) with a rolling row' },
      },
    ],
  },
  {
    id: 'interleaving-string',
    title: 'Interleaving String',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Naive Recursion',
        explanation: 'At each pair of positions (how far along s1, how far along s2), recursively try consuming the next character from EITHER string, as long as it matches the next character s3 is expecting. Correct, but "can s3 still be completed from this exact pair of positions" is re-asked by every different earlier interleaving choice that reaches the same pair.',
        steps: [
          'Track how far along s1 (index i) and s2 (index j) the interleaving has consumed — together, i + j is always the position reached in s3.',
          'Base case: both strings fully consumed means s3 was fully matched — success.',
          'Try consuming the next s1 character, if it matches s3\'s next expected character.',
          'Try consuming the next s2 character, if IT matches — either branch succeeding is enough.',
        ],
        code:
`boolean isInterleaveBasic(String s1, String s2, String s3, int i, int j) {
    if (i == s1.length() && j == s2.length()) return true;
    boolean fromS1 = i < s1.length() && s1.charAt(i) == s3.charAt(i + j)
        && isInterleaveBasic(s1, s2, s3, i + 1, j);
    boolean fromS2 = j < s2.length() && s2.charAt(j) == s3.charAt(i + j)
        && isInterleaveBasic(s1, s2, s3, i, j + 1);
    return fromS1 || fromS2;
}`,
        complexity: { time: 'O(2^(m+n))', space: 'O(m + n) — recursion depth' },
      },
      {
        name: 'Optimized — Bottom-Up Boolean Grid DP',
        explanation: 'Build dp[i][j] — true if s3\'s first (i+j) characters can be formed by interleaving s1\'s first i characters with s2\'s first j characters. Each cell only ever needs the cell above (consumed one more s1 character) or the cell to the left (consumed one more s2 character).',
        steps: [
          'dp[0][0] = true — two empty prefixes trivially interleave into an empty result.',
          'dp[i][j] is true if EITHER: dp[i-1][j] is true and s1[i-1] matches the expected s3 character, OR dp[i][j-1] is true and s2[j-1] matches it.',
          'The expected s3 character at cell (i,j) is always at index i+j-1 — the total characters consumed so far.',
          'Return dp[m][n] (after first checking that lengths m + n = s3.length(), or the answer is immediately false).',
        ],
        visual: {
          arrays: [
            { label: 's1 = "aab", s2 = "dbc" (row = s1 index)', values: ['T', 'F', 'F', 'F'] },
            { label: 'row 1', values: ['T', null, null, null] },
            { label: 'row 2', values: ['F', null, null, null] },
          ],
          varNames: [],
          steps: [
            { note: 's3="aadbbc" (interleaving of "aab" and "dbc"). Row 0 uses only s2: dp[0][1]: s2[0]=\'d\' vs s3[0]=\'a\' → false, so the rest of row 0 stays false too.' },
            { snapshots: { 'row 1': ['T', 'F', null, null] }, highlights: { 'row 1': [1] }, note: 'dp[1][1]: from above needs s1[0]=\'a\'==s3[1]=\'a\' and dp[0][1]=false → fails. From left needs s2[0]=\'d\'==s3[1]=\'a\' → fails. dp[1][1] = false.' },
            { note: 'Continuing this way across both rows (matching the full hand-traced grid), the interleaving succeeds via a path that alternates between consuming from s1 and s2 exactly where their next character matches s3\'s next expected one.' },
            { snapshots: { 'row 2': ['F', 'T', 'T', 'T'] }, highlights: { 'row 2': [3] }, note: 'dp[3][3] (bottom-right, after processing the full grid) = true — s3 IS a valid interleaving of s1 and s2.' },
          ],
        },
        code:
`boolean isInterleave(String s1, String s2, String s3) {
    int m = s1.length(), n = s2.length();
    if (m + n != s3.length()) return false;

    boolean[][] dp = new boolean[m + 1][n + 1];
    dp[0][0] = true;
    for (int i = 0; i <= m; i++) {
        for (int j = 0; j <= n; j++) {
            if (i == 0 && j == 0) continue;
            boolean fromS1 = i > 0 && dp[i - 1][j] && s1.charAt(i - 1) == s3.charAt(i + j - 1);
            boolean fromS2 = j > 0 && dp[i][j - 1] && s2.charAt(j - 1) == s3.charAt(i + j - 1);
            dp[i][j] = fromS1 || fromS2;
        }
    }
    return dp[m][n];
}`,
        complexity: { time: 'O(m · n)', space: 'O(m · n) — or O(n) with a rolling row' },
      },
    ],
  },
];

export default dp2dDrills;
